import React from 'react';

import { AssetManager } from '../../../../viewer/asset-manager';
import { SubscriptionTopic } from '../../../../viewer/messaging/pubsub';
import { useNonce } from '../../../hooks/use-nonce';
import { useSubscribe } from '../../../hooks/use-subscribe';

interface CategoryContext {
  expandedAssetId?: string;
  toggle: (assetId: string) => void;
  refreshAssets: () => void;
  refreshNonce: number;
}

const CategoryContext = React.createContext<CategoryContext>(undefined!);

type Props = {
  children: React.ReactNode;
};

const CategoryState = (props: Props) => {
  const [expandedAssetId, setExpandedAssetId] = React.useState<string>();
  const [refreshNonce, refresh] = useNonce();

  useSubscribe(SubscriptionTopic.NewSelection, (asset) => {
    setExpandedAssetId(asset.uuid);
  });

  useSubscribe(SubscriptionTopic.UnSelection, () => {
    setExpandedAssetId(undefined);
  });

  // On first load, check if any asset is selected in asset manager (for instance after timeline
  // change). Then retrig selection of the same asset in order to have asset selected in the asset
  // menu.
  React.useEffect(() => {
    if (AssetManager.instance.getSelected()) {
      AssetManager.instance.setSelected(AssetManager.instance.getSelected());
    }
  }, []);

  React.useEffect(() => {
    if (expandedAssetId) {
      AssetManager.instance.selectByUuid(expandedAssetId);
    } else {
      AssetManager.instance.unselect();
      AssetManager.instance.setSelected(undefined);
    }
  }, [expandedAssetId]);

  // Stable function reference — avoids a new object identity on every render,
  // which would otherwise cause every CategoryContext consumer (i.e. every asset
  // row) to re-render even when nothing relevant changed.
  const toggle = React.useCallback((assetId: string) => {
    setExpandedAssetId((current) => (current === assetId ? undefined : assetId));
  }, []);

  // Memoize the context value so consumers only re-render when one of the four
  // values actually changes, not on every parent render.
  const contextValue = React.useMemo(
    () => ({ expandedAssetId, toggle, refreshAssets: refresh, refreshNonce }),
    [expandedAssetId, toggle, refresh, refreshNonce],
  );

  return <CategoryContext.Provider value={contextValue}>{props.children}</CategoryContext.Provider>;
};

export { CategoryContext, CategoryState };
